import { useState } from "react";
import { Plus, Pencil, KeyRound, Copy, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { StatusChip, LifecycleDialog, ArchiveDemoButton } from "@/components/UserLifecycle";
import { toast } from "sonner";
import { PageHeader, Loading, Field, NSelect, Chip, DemoTag, Plate, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = { admin: "Administrateur / Direction", direction: "Direction (consultation)", chef_depot: "Chef de dépôt / Logistique", commercial: "Commercial", chauffeur: "Chauffeur / Logistique", administratif: "Administratif (documents & permis)" };

const genPwd = () => {
  const a = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz";
  const r = (s) => s[Math.floor(Math.random() * s.length)];
  return "Ph" + Array.from({ length: 6 }, () => r(a + "23456789")).join("") + r("23456789") + r(a);
};

function ResetDialog({ u, onClose }) {
  const [pwd, setPwd] = useState("");
  const [done, setDone] = useState(null);
  const go = async () => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, { password: pwd || null });
      setDone(data.temporary_password);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="reset-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">Réinitialiser le mot de passe — {u.name}</DialogTitle></DialogHeader>
        {done ? (
          <div className="space-y-3" data-testid="reset-result">
            <p className="text-sm text-slate-600">Mot de passe provisoire à transmettre (affiché une seule fois). L'utilisateur devra le remplacer à sa prochaine connexion.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-lg font-bold bg-[#FEF3E2] border border-[#F49619]/40 rounded-md px-3 py-2" data-testid="reset-temp-password">{done}</code>
              <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(done); toast.success("Copié"); }} data-testid="reset-copy"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Le mot de passe actuel sera remplacé par un mot de passe provisoire. Laissez vide pour en générer un automatiquement.</p>
            <Field label="Mot de passe provisoire (facultatif)"><Input value={pwd} onChange={(e) => setPwd(e.target.value)} className="font-mono" data-testid="reset-password-input" /></Field>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reset-close">{done ? "Fermer" : "Annuler"}</Button>
          {!done && <Button onClick={go} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="reset-confirm">Réinitialiser</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDialog({ u, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", role: "commercial", password: "", active: true, can_view_history: false, manual_reading_allowed: false, job_title: "", ...u, password: "" });
  const set = (k) => (x) => setF({ ...f, [k]: x?.target ? x.target.value : x });
  const save = async () => {
    try {
      const body = { ...f, password: u.id ? null : f.password || null };
      if (u.id) await api.put(`/users/${u.id}`, body);
      else await api.post("/users", body);
      toast.success("Utilisateur enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#0E2D43]">{u.id ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Field label="Nom"><Input value={f.name} onChange={set("name")} data-testid="user-name-input" /></Field>
          <Field label="Fonction (affichée dans les modules)"><Input list="job-titles" value={f.job_title || ""} onChange={set("job_title")} data-testid="user-job-title-input" />
            <datalist id="job-titles">{["Commercial", "Technicien", "Chef de dépôt", "Chauffeur livreur", "Direction", "Administratif"].map((x) => <option key={x} value={x} />)}</datalist></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} data-testid="user-email-input" /></Field>
          <Field label="Rôle (droits d'accès)"><NSelect value={f.role} onChange={set("role")} testid="user-role-select" options={Object.entries(ROLES).map(([k, v]) => ({ value: k, label: v }))} />
            <p className="text-[11px] text-slate-500 mt-1">Le rôle fixe ce que la personne peut voir et faire ; la fonction est l'intitulé affiché (ex. Ivan : rôle Commercial pour son espace véhicule, fonction Technicien).</p></Field>
          {!u.id && (
            <Field label="Mot de passe provisoire (8 caractères min., lettres + chiffres)">
              <div className="flex gap-2">
                <Input value={f.password} onChange={set("password")} className="font-mono" data-testid="user-password-input" />
                <Button type="button" variant="outline" onClick={() => setF({ ...f, password: genPwd() })} data-testid="user-generate-password">Générer</Button>
              </div>
              <span className="text-xs text-slate-500 normal-case tracking-normal font-normal">À transmettre à l'utilisateur : il devra le remplacer à sa première connexion.</span>
            </Field>
          )}
          <label className="flex items-center justify-between text-sm"><span>Compte actif</span><Switch checked={f.active} onCheckedChange={set("active")} data-testid="user-active-switch" /></label>
          {f.role === "commercial" && (
            <label className="flex items-center justify-between text-sm"><span>Autoriser la consultation de son historique</span>
              <Switch checked={f.can_view_history} onCheckedChange={set("can_view_history")} data-testid="user-history-switch" /></label>
          )}
          {f.role === "commercial" && (
            <label className="flex items-center justify-between text-sm"><span>Autoriser exceptionnellement la saisie manuelle du kilométrage</span>
              <Switch checked={!!f.manual_reading_allowed} onCheckedChange={set("manual_reading_allowed")} data-testid="user-manual-reading-switch" /></label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="user-save-button">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [show, setShow] = useState("courants");
  const { data: all, loading, reload } = useFetch("/users", { include_archived: true });
  const [edit, setEdit] = useState(null);
  const [reset, setReset] = useState(null);
  const [life, setLife] = useState(null);
  const st = (u) => u.status || (u.active ? "actif" : "inactif");
  const data = (all || []).filter((u) => (show === "courants" ? ["actif", "inactif"].includes(st(u)) : show === "tous" ? true : st(u) === show));
  const demoCount = (all || []).filter((u) => u.is_demo && ["actif", "inactif"].includes(st(u))).length;
  return (
    <div data-testid="users-page">
      <PageHeader overline="Paramètres" title="Utilisateurs">
        {me.canEdit && <ArchiveDemoButton count={demoCount} onDone={reload} />}
        {me.canEdit && <button onClick={() => setEdit({})} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] text-sm font-bold transition-colors" data-testid="user-create-button">
          <Plus className="w-4 h-4" /> Nouvel utilisateur
        </button>}
      </PageHeader>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="users-status-filter">
        {[["courants", "Actifs & inactifs"], ["actif", "Actifs"], ["inactif", "Inactifs"], ["archive", "Archivés"], ["supprime", "Supprimés"], ["tous", "Tous"]].map(([k, l]) => (
          <button key={k} onClick={() => setShow(k)} data-testid={`users-filter-${k}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${show === k ? "bg-[#0E2D43] text-white border-[#0E2D43]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{l}</button>
        ))}
        <span className="text-xs text-slate-500 ml-2">Pour un salarié qui part ou change de fonction, privilégiez Désactiver (modifier) ou Archiver.</span>
      </div>
      {loading ? <Loading /> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto rise">
          <table className="tbl w-full" data-testid="users-table">
            <thead><tr><th>Nom</th><th>Email</th><th>Rôle</th><th>Véhicule(s)</th><th>Historique</th><th>Connexion</th><th>Statut</th><th></th></tr></thead>
            <tbody>
              {data.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Aucun utilisateur</td></tr>}
              {data.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email || u.id}`}>
                  <td className="font-semibold text-[#0E2D43]">{u.name} <DemoTag show={u.is_demo} /></td>
                  <td>{st(u) === "supprime" ? <span className="text-slate-400">— (identifiants supprimés)</span> : u.email}</td><td>{ROLES[u.role]}</td>
                  <td className="space-x-1">{u.vehicles.map((v) => <Plate key={v.id}>{v.plate}</Plate>)}</td>
                  <td>{u.role === "commercial" ? (u.can_view_history ? "Autorisé" : "Non autorisé") : "—"}</td>
                  <td>{["supprime", "archive"].includes(st(u)) ? "—" : u.must_change_password ? <Chip cls="bg-amber-50 text-amber-700 border-amber-200" testid={`user-pending-${u.email || u.id}`}>Première connexion en attente</Chip> : <Chip cls="bg-[#0E2D43] text-white border-[#0E2D43]" testid={`user-activated-${u.email || u.id}`}>Compte activé</Chip>}</td>
                  <td><StatusChip u={u} /></td>
                  <td>{me.canEdit && <div className="flex gap-1">
                    {["actif", "inactif"].includes(st(u)) && <button onClick={() => setEdit(u)} className="p-1.5 rounded hover:bg-slate-100" title="Modifier / désactiver" data-testid={`user-edit-${u.email || u.id}`}><Pencil className="w-4 h-4" /></button>}
                    {u.id !== me.id && u.role !== "admin" && ["actif", "inactif"].includes(st(u)) && <button onClick={() => setReset(u)} className="p-1.5 rounded hover:bg-slate-100" title="Réinitialiser le mot de passe" data-testid={`user-reset-${u.email || u.id}`}><KeyRound className="w-4 h-4" /></button>}
                    {u.role !== "admin" && ["actif", "inactif"].includes(st(u)) && <button onClick={() => setLife(["archive", u])} className="p-1.5 rounded hover:bg-violet-50 text-violet-700" title="Archiver" data-testid={`user-archive-${u.email || u.id}`}><Archive className="w-4 h-4" /></button>}
                    {st(u) === "archive" && <button onClick={() => setLife(["restore", u])} className="p-1.5 rounded hover:bg-slate-100" title="Restaurer" data-testid={`user-restore-${u.email || u.id}`}><ArchiveRestore className="w-4 h-4" /></button>}
                    {u.role !== "admin" && st(u) !== "supprime" && <button onClick={() => setLife(["delete", u])} className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Supprimer définitivement" data-testid={`user-delete-${u.email || u.id}`}><Trash2 className="w-4 h-4" /></button>}
                  </div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {life && <LifecycleDialog action={life[0]} u={life[1]} onClose={() => setLife(null)} onDone={() => { setLife(null); reload(); }} />}
      {reset && <ResetDialog u={reset} onClose={() => { setReset(null); reload(); }} />}
      {edit && <UserDialog u={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </div>
  );
}
